import { InputHTMLAttributes, useEffect, useRef, useState } from "react";
import { FiEdit2, FiEye, FiEyeOff, FiInfo } from "react-icons/fi";
import { FormLabel } from "../FormLabel";
import ReactTooltip from "react-tooltip";
import { ButtonWithIcon } from "newui/components/Button";
import FormikFieldError from "../FormikFieldError";

export interface BaseInputWithLabelProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
	label?: string;
	placeholderSize?: string;
	required?: boolean;
	placeholder?: string;
	className?: string;
	inputClassName?: string;
	icon?: boolean;
	customIcon?: React.ReactNode;
	type?: string;
	rfx?: boolean;
	formatValue?: (value: string) => string;
	disableToggler?: boolean;
	errorText?: string;
	hasError?: boolean;
	guideLine?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	iconPosition?: "left" | "right";
	testId?: string;
	flexGrow?: number;
	noMargin?: boolean;
	fixedPlaceholder?: boolean;
	rfxIcon?: React.ReactNode;
	handleToggler?: (value: boolean) => void;
	value?: string;
}

export const InputWithLabel: React.FC<BaseInputWithLabelProps> = ({
	label,
	placeholderSize,
	required,
	placeholder,
	className = "",
	inputClassName = "",
	icon,
	customIcon,
	type = "text",
	rfx,
	formatValue,
	disableToggler,
	errorText,
	hasError = false,
	guideLine,
	onChange,
	iconPosition,
	testId,
	flexGrow,
	noMargin,
	fixedPlaceholder,
	rfxIcon,
	handleToggler,
	value,
	disabled,
	id,
	name
}) => {
	const [visible, setVisible] = useState(false);
	const [pHolder, setPHolder] = useState(placeholder);
	const inputElement = useRef<HTMLInputElement>(null);
	const [toggleButton, setToggleButton] = useState(
		disableToggler || disabled ? true : false
	);

	useEffect(() => setToggleButton(disabled), [disabled]);

	const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!formatValue) {
			return;
		}

		let val = (e.target as HTMLInputElement).value;
		let key = e.keyCode || (e as any).charCode;

		if (key === 8) {
			return;
		}

		val = formatValue(val);
		if (formatValue && inputElement.current) {
			let val = e.currentTarget.value;
			let key = e.keyCode || (e as any).charCode;

			if (key === 8) {
				return;
			}

			val = formatValue(val);
			inputElement.current.value = val;
		}
	};

	const onToggleButton = () => {
		if (handleToggler) {
			handleToggler(!toggleButton);
		}

		setToggleButton(!toggleButton);
		setPHolder("");
		inputElement.current?.focus();
	}

	const hasInputError = !!errorText || hasError;

	return (
		<div
			className={`relative transition-all duration-150 ${hasInputError ? 'pb-2' : ''} ${className} ${noMargin ? '!mb-0' : 'mb-4'}`}
			style={{ flexGrow: flexGrow || 0 }}
		>
			{label && (
				<FormLabel
					htmlFor={id || name}
					label={label}
					required={required}
				>
					{guideLine && (
						<div className={rfx ? "inline-block" : ""}>
							<FiInfo // InfoIcon
								className="cursor-pointer ml-1"
								size={16}
								data-tip
								data-for={name}
							/>
							<ReactTooltip
								id={name}
								place="right"
								effect="solid"
								className="!border !border-gray-400 !rounded !text-xs !normal-case !px-2 !py-1 !font-normal !max-w-md"
								arrowColor="transparent"
							>
								{guideLine}
							</ReactTooltip>
						</div>
					)}
				</FormLabel>
			)}

			{disableToggler && toggleButton && (
				<div className="absolute right-8 top-9">
					<ButtonWithIcon
						icon={<FiEdit2 />} // Pencil
						text="CHANGE"
						btntype="secondary"
						width="8rem"
						onClick={onToggleButton}
					/>
				</div>
			)}

			{icon && !toggleButton && (
				<>
					{customIcon ? (
						<div className="absolute left-5 top-11 cursor-pointer">
							{customIcon}
						</div>
					) : (
						<div className={`absolute cursor-pointer ${rfx ? 'right-5 top-10' : 'right-8 top-11'}`}>
							{!visible ? (
								<FiEye onClick={() => setVisible(true)} size={20} /> // OpenEye
							) : (
								<FiEyeOff onClick={() => setVisible(false)} size={20} /> // CloseEye
							)}
						</div>
					)}
				</>
			)}

			{rfxIcon && (
				<div className="absolute left-3 top-10">
					{rfxIcon}
				</div>
			)}

			<input
				ref={inputElement}
				onKeyUp={onKeyUp}
				placeholder={fixedPlaceholder ? placeholder : pHolder}
				onChange={onChange}
				type={icon && visible ? "text" : type}
				className={`
          w-full h-14 max-h-14 px-4 py-4 
          bg-white border rounded-md outline-none
          transition-all duration-150
          placeholder:text-gray-500
          disabled:cursor-not-allowed disabled:hover:bg-gray-300 disabled:focus:bg-gray-300
          hover:outline-none hover:border-blue-400
          ${hasInputError
						? 'border-red-300 focus:border-red-300 focus:ring-4 focus:ring-red-100'
						: 'border-gray-200 focus:border-blue-300 focus:ring-4 focus:ring-blue-100'
					}
          ${inputClassName}
        `}
				style={{
					paddingLeft: iconPosition === "left" || rfxIcon ? "50px" : "16px",
					paddingRight: "16px",
					fontSize: placeholderSize || undefined,
				}}
				value={value}
				disabled={disabled}
				id={id}
				name={name}
				data-testid={testId}
			/>

			<FormikFieldError text={errorText} />
		</div>
	);
};